import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/components/auth/AuthProvider";
import { Separator } from "@/components/ui/separator";
import {
  type NursingHomeClaimFormData,
  isNursingHomeClaimFormComplete,
  FACILITY_RELATIONSHIP_LABELS,
  REFERRAL_SOURCE_OPTIONS,
} from "@/lib/nursing-home-claim-form";

export type ClaimSelectField =
  | "role"
  | "facilityRelationship"
  | "verificationChannel"
  | "referralSource";

interface ClaimDialogProps {
  centerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  formData: NursingHomeClaimFormData;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectFieldChange: (field: ClaimSelectField, value: string) => void;
  patchFormData: (patch: Partial<NursingHomeClaimFormData>) => void;
  onSubmit: () => Promise<void>;
  onShowRegistration?: () => void;
}

export function ClaimDialog({
  centerName,
  open,
  onOpenChange,
  isSubmitting,
  formData,
  onInputChange,
  onSelectFieldChange,
  patchFormData,
  onSubmit,
  onShowRegistration,
}: ClaimDialogProps) {
  const { user } = useAuth();

  const isFormValid = isNursingHomeClaimFormComplete(formData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      sessionStorage.setItem(
        "pendingClaimData",
        JSON.stringify({
          centerName,
          formData,
          nursingHomeSlug: window.location.pathname.split("/").pop(),
        }),
      );
      onOpenChange(false);
      onShowRegistration?.();
      return;
    }

    await onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Claim {centerName}</DialogTitle>
          <DialogDescription>
            {!user
              ? "Complete all sections. You’ll create an account next so we can verify your request."
              : "We use these details to verify you’re authorised to manage this listing. Incomplete claims may be delayed."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 py-2">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Your role & contact</h4>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="role">Position at the facility</Label>
                  <Select
                    name="role"
                    value={formData.role}
                    onValueChange={(v) => onSelectFieldChange("role", v)}
                    required
                  >
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="administrator">Administrator</SelectItem>
                      <SelectItem value="director">Director of Care</SelectItem>
                      <SelectItem value="staff">Staff Member</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="legalName">Full legal name</Label>
                  <Input
                    id="legalName"
                    name="legalName"
                    value={formData.legalName}
                    onChange={onInputChange}
                    placeholder="As on ID or employment records"
                    required
                    autoComplete="name"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="jobTitle">Job title</Label>
                  <Input
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={onInputChange}
                    placeholder="e.g. Nursing Home Manager"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={onInputChange}
                    placeholder="your.name@facility-or-group.com"
                    required
                    autoComplete="email"
                  />
                  <p className="text-xs text-muted-foreground">
                    A matching domain often speeds up verification.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="contactPhone">Direct phone (incl. country code)</Label>
                  <Input
                    id="contactPhone"
                    name="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={onInputChange}
                    placeholder="+353…"
                    required
                    autoComplete="tel"
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Preferred follow-up for verification</Label>
                  <Select
                    value={formData.verificationChannel}
                    onValueChange={(v) =>
                      onSelectFieldChange(
                        "verificationChannel",
                        v as NursingHomeClaimFormData["verificationChannel"],
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="either">Email or phone</SelectItem>
                      <SelectItem value="email">Email only</SelectItem>
                      <SelectItem value="phone">Phone only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Organisation & authority</h4>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="organizationLegalName">Legal name of operator / employer</Label>
                  <Input
                    id="organizationLegalName"
                    name="organizationLegalName"
                    value={formData.organizationLegalName}
                    onChange={onInputChange}
                    placeholder="Registered company or entity that operates this home"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Your relationship to this facility</Label>
                  <Select
                    value={formData.facilityRelationship}
                    onValueChange={(v) => onSelectFieldChange("facilityRelationship", v)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select the option that best describes you" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(FACILITY_RELATIONSHIP_LABELS) as Array<
                        keyof typeof FACILITY_RELATIONSHIP_LABELS
                      >).map((key) => (
                        <SelectItem key={key} value={key}>
                          {FACILITY_RELATIONSHIP_LABELS[key]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="officialWebsiteUrl">Official facility website (optional)</Label>
                  <Input
                    id="officialWebsiteUrl"
                    name="officialWebsiteUrl"
                    type="url"
                    value={formData.officialWebsiteUrl}
                    onChange={onInputChange}
                    placeholder="https://…"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="hiqaOrRegistrationRef">
                    HIQA / registration / corporate reference (optional)
                  </Label>
                  <Input
                    id="hiqaOrRegistrationRef"
                    name="hiqaOrRegistrationRef"
                    value={formData.hiqaOrRegistrationRef}
                    onChange={onInputChange}
                    placeholder="Any ID that helps us match the legal entity"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="linkedInUrl">LinkedIn profile URL (optional)</Label>
                  <Input
                    id="linkedInUrl"
                    name="linkedInUrl"
                    type="url"
                    value={formData.linkedInUrl}
                    onChange={onInputChange}
                    placeholder="https://linkedin.com/in/…"
                  />
                </div>

                <div className="flex items-start gap-3 rounded-md border border-muted p-3">
                  <Checkbox
                    id="authorityConfirmation"
                    checked={formData.authorityConfirmation}
                    onCheckedChange={(c) =>
                      patchFormData({ authorityConfirmation: c === true })
                    }
                  />
                  <div className="grid gap-1">
                    <Label htmlFor="authorityConfirmation" className="font-medium leading-snug">
                      I confirm I am authorised to manage this Grandpal listing on behalf of the
                      facility named above.
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      False claims may be rejected and accounts restricted.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Anything else</h4>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>How did you hear about Grandpal? (optional)</Label>
                  <Select
                    value={formData.referralSource || "unset"}
                    onValueChange={(v) => onSelectFieldChange("referralSource", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRAL_SOURCE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="verificationNote">Additional context for our review team</Label>
                  <Textarea
                    id="verificationNote"
                    name="verificationNote"
                    value={formData.verificationNote}
                    onChange={onInputChange}
                    placeholder="e.g. group structure, who to contact in head office, documents you can provide…"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !isFormValid}>
              {isSubmitting
                ? "Submitting…"
                : !user
                  ? "Create account & submit"
                  : "Submit claim request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
